import React, { useEffect, useState, useCallback } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import {
  Box, Chip, MenuItem, Skeleton, Stack, TableBody, TableCell, TableHead, TablePagination, TableRow, TextField,
  useMediaQuery, useTheme,
} from '@mui/material';
import { DeleteOutlined as DeleteIcon, EditOutlined as EditIcon } from '@mui/icons-material';
import dayjs from 'dayjs';
import {
  ConfirmDeleteDialog, DataTable, DatagridEmpty, FilterToggle, ListScreen, PageTitle, RecordCardList, RowActionsMenu,
  SectionHeader,
} from '@/components/shell';
import type { IOcrTestCase } from '@/types';
import { OCR_OPERATION_TYPES } from '@/config/constants';
import { HttpError } from '@/utils/httpClient';
import { ocrTestingService } from './ocrTestingService';
import { useOcrLabels } from './ocrLabels';

const PAGE_SIZE = 25;
const COLUMNS = 7;

type ValidationFilter = '' | 'true' | 'false';

/** Validated is the normal state; a draft is the exception, so only a draft gets a chip. */
const CaseStatus: React.FC<{ testCase: IOcrTestCase }> = ({ testCase }) => {
  const t = useTranslate();
  if (testCase.isValidated) return <>{t('screens.ocrTesting.validated')}</>;
  return <Chip label={t('screens.ocrTesting.draft')} color="warning" size="small" />;
};

export const TestCasesList: React.FC = () => {
  const t = useTranslate();
  const notify = useNotify();
  const navigate = useNavigate();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const labels = useOcrLabels();

  const [cases, setCases] = useState<IOcrTestCase[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterOpType, setFilterOpType] = useState<number | ''>('');
  const [filterNationality, setFilterNationality] = useState('');
  const [filterValidation, setFilterValidation] = useState<ValidationFilter>('');
  const [deleteTarget, setDeleteTarget] = useState<IOcrTestCase | null>(null);

  const fetchCases = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await ocrTestingService.listCases({
        page: page + 1,
        pageSize: PAGE_SIZE,
        operationType: filterOpType || undefined,
        nationalityCode: filterNationality || undefined,
        isValidated: filterValidation === '' ? undefined : filterValidation === 'true',
      });
      setCases(data.data);
      setTotal(data.recordsTotal);
    } catch {
      notify('screens.ocrTesting.loadCasesFailed', { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, filterOpType, filterNationality, filterValidation, notify]);

  useEffect(() => { fetchCases(); }, [fetchCases]);

  const handleRunAll = async () => {
    try {
      const { data: run } = await ocrTestingService.runAll(filterOpType || undefined);
      notify('screens.ocrTesting.runStarted', { type: 'success' });
      navigate(`/ocr-testing/runs/${run.id}`);
    } catch (e) {
      if (e instanceof HttpError && e.status === 409) notify('screens.ocrTesting.runAlreadyInProgress', { type: 'warning' });
      else notify('screens.ocrTesting.runStartFailed', { type: 'error' });
    }
  };

  const openCase = (tc: IOcrTestCase) => navigate(`/ocr-testing/cases/${tc.id}`);
  const activeFilters = (filterOpType ? 1 : 0) + (filterNationality ? 1 : 0) + (filterValidation ? 1 : 0);

  const rowActions = (tc: IOcrTestCase) => (
    <RowActionsMenu
      actions={[
        { key: 'edit', label: t('ra.action.edit'), icon: <EditIcon fontSize="small" />, onClick: () => openCase(tc) },
        {
          key: 'delete',
          label: t('ra.action.delete'),
          icon: <DeleteIcon fontSize="small" />,
          onClick: () => setDeleteTarget(tc),
          color: 'error',
          dividerBefore: true,
        },
      ]}
    />
  );

  const empty = <DatagridEmpty title={t('screens.ocrTesting.noCases')} hint={t('screens.ocrTesting.noCasesHint')} />;

  const pagination = (
    <TablePagination
      component="div"
      count={total}
      page={page}
      onPageChange={(_, p) => setPage(p)}
      rowsPerPage={PAGE_SIZE}
      rowsPerPageOptions={[]}
    />
  );

  const filterSx = { minWidth: 200, flex: { xs: '1 1 100%', sm: '0 1 auto' } };

  const header = (
    <Box>
      <SectionHeader
        left={<PageTitle title={t('screens.ocrTesting.casesTitle')} subtitle={t('screens.ocrTesting.casesSubtitle')} />}
        filters={<FilterToggle open={filtersOpen} onToggle={() => setFiltersOpen((o) => !o)} activeCount={activeFilters} />}
        onRefresh={fetchCases}
        refreshing={loading}
        actions={[{ label: t('screens.ocrTesting.runTests'), onClick: handleRunAll }]}
        create={{ label: t('screens.ocrTesting.newCase'), onClick: () => navigate('/ocr-testing/cases/new') }}
      />
      {filtersOpen && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, pb: 1.5 }}>
          <TextField
            select
            size="small"
            label={t('screens.ocrTesting.operationType')}
            value={filterOpType}
            onChange={(e) => { setFilterOpType(e.target.value === '' ? '' : Number(e.target.value)); setPage(0); }}
            sx={filterSx}
          >
            <MenuItem value="">{t('screens.ocrTesting.all')}</MenuItem>
            {OCR_OPERATION_TYPES.map((op) => (
              <MenuItem key={op.id} value={op.id}>{labels.operationType(op.id)}</MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label={t('screens.ocrTesting.nationality')}
            value={filterNationality}
            onChange={(e) => { setFilterNationality(e.target.value); setPage(0); }}
            sx={filterSx}
          />
          <TextField
            select
            size="small"
            label={t('screens.ocrTesting.status')}
            value={filterValidation}
            onChange={(e) => { setFilterValidation(e.target.value as ValidationFilter); setPage(0); }}
            sx={filterSx}
          >
            <MenuItem value="">{t('screens.ocrTesting.all')}</MenuItem>
            <MenuItem value="true">{t('screens.ocrTesting.validated')}</MenuItem>
            <MenuItem value="false">{t('screens.ocrTesting.draft')}</MenuItem>
          </TextField>
        </Box>
      )}
    </Box>
  );

  const deleteDialog = (
    <ConfirmDeleteDialog
      open={Boolean(deleteTarget)}
      onClose={() => setDeleteTarget(null)}
      name={deleteTarget?.name ?? ''}
      onDelete={async () => {
        if (!deleteTarget) return;
        await ocrTestingService.deleteCase(deleteTarget.id);
        fetchCases();
      }}
    />
  );

  if (isPhone) {
    return (
      <ListScreen header={header}>
        {loading ? (
          <Stack spacing={1}>{[...Array(4)].map((_, i) => <Skeleton key={i} variant="rounded" height={96} />)}</Stack>
        ) : (
          <>
            <RecordCardList
              records={cases}
              getKey={(tc) => tc.id}
              empty={empty}
              mobile={{
                primary: (tc) => tc.name,
                status: (tc) => <CaseStatus testCase={tc} />,
                lines: [
                  { label: t('screens.ocrTesting.operation'), render: (tc) => labels.operationType(tc.operationType) },
                  { label: t('screens.ocrTesting.documentType'), render: (tc) => tc.documentTypeName || '—' },
                  { label: t('screens.ocrTesting.nationality'), render: (tc) => tc.nationalityCode || '—' },
                ],
                actions: rowActions,
                onClick: openCase,
              }}
            />
            {total > PAGE_SIZE && pagination}
          </>
        )}
        {deleteDialog}
      </ListScreen>
    );
  }

  return (
    <ListScreen header={header}>
      <DataTable
        fill
        label={t('screens.ocrTesting.casesTitle')}
        empty={!loading && cases.length === 0 ? empty : undefined}
        footer={total > PAGE_SIZE ? pagination : undefined}
      >
        <TableHead>
          <TableRow>
            <TableCell>{t('screens.ocrTesting.name')}</TableCell>
            <TableCell>{t('screens.ocrTesting.documentType')}</TableCell>
            <TableCell>{t('screens.ocrTesting.nationality')}</TableCell>
            <TableCell>{t('screens.ocrTesting.operation')}</TableCell>
            <TableCell>{t('screens.ocrTesting.status')}</TableCell>
            <TableCell>{t('screens.ocrTesting.created')}</TableCell>
            <TableCell align="right" aria-label={t('shell.rowActions.more')} />
          </TableRow>
        </TableHead>
        <TableBody>
          {loading
            ? [...Array(6)].map((_, i) => (
              <TableRow key={i}>
                {[...Array(COLUMNS)].map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}
              </TableRow>
            ))
            : cases.map((tc) => (
              <TableRow key={tc.id} onClick={() => openCase(tc)} sx={{ cursor: 'pointer' }}>
                <TableCell>{tc.name}</TableCell>
                <TableCell>{tc.documentTypeName || '—'}</TableCell>
                <TableCell>{tc.nationalityCode || '—'}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{labels.operationType(tc.operationType)}</TableCell>
                <TableCell><CaseStatus testCase={tc} /></TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{dayjs(tc.createdAt).format('DD/MM/YYYY')}</TableCell>
                <TableCell align="right" onClick={(e) => e.stopPropagation()}>{rowActions(tc)}</TableCell>
              </TableRow>
            ))}
        </TableBody>
      </DataTable>
      {deleteDialog}
    </ListScreen>
  );
};
